import { HttpClient } from '@angular/common/http';
import { Injectable, effect, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { InsightsService, TrackingEvent } from 'shared/domain';

import { TreetopPropertiesProvider } from './treetop-properties-provider';

@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private insightsService = inject(InsightsService);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  // Shows whether the application is currently online or offline
  private _isOnline = signal<boolean>(false);
  public isOnline = this._isOnline.asReadonly();

  // Shows whether the application is online by default (on initial app load)
  public _defaultOnline = signal(false);
  public defaultOnline = this._defaultOnline.asReadonly();

  private initialized = false;

  constructor() {
    effect(() => {
      const isOnline = this._isOnline();

      if (!this.initialized) {
        return;
      }

      const route = this.router.url;
      this.insightsService.trackEvent(TrackingEvent.NETWORK_STATUS_CHANGED, { isOnline, route });

      console.warn(`Network status changed: ${this._isOnline() ? 'Online' : 'Offline'}`);
    });
  }

  async init(): Promise<boolean> {
    window.addEventListener('online', () => this.checkConnection());
    window.addEventListener('offline', () => this.updateOnlineStatus(false));

    const defaultOnline = await this.checkConnection();
    this._defaultOnline.set(defaultOnline);

    this.initialized = true;

    return defaultOnline;
  }

  async checkConnection(): Promise<boolean> {
    const pingUrl = `${this.baseUrl}/version`;

    const online = await firstValueFrom(
      this.http.get(pingUrl).pipe(
        map(() => true),
        catchError(() => of(false)),
      ),
    );

    this.updateOnlineStatus(online);

    return online;
  }

  private updateOnlineStatus(isOnline: boolean) {
    this._isOnline.set(isOnline);
  }
}
